/**
 * Building and validating library snapshots.
 *
 * Dependency-free so it can run in the browser (validating cached data) as
 * well as on the server (mapping database rows).
 */
import type {
  LibrarySnapshot,
  MovieId,
  RatingEntry,
  WatchedEntry,
  WatchlistEntry,
} from "@/types/library";

/** A TMDB id: a positive integer without leading zeros. */
export const MOVIE_ID_PATTERN = /^[1-9][0-9]{0,11}$/;

export function isMovieId(value: unknown): value is MovieId {
  return typeof value === "string" && MOVIE_ID_PATTERN.test(value);
}

export function isRatingValue(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= 10
  );
}

/** Normalize a timestamp to `toISOString()` form, or null if invalid. */
export function toIsoTimestamp(value: unknown): string | null {
  if (typeof value !== "string" && !(value instanceof Date)) return null;
  const time = new Date(value).getTime();

  return Number.isFinite(time) ? new Date(time).toISOString() : null;
}

export interface WatchlistRow {
  movie_id: string;
  added_at: string;
}

export interface WatchedRow {
  movie_id: string;
  watched_at: string;
}

export interface RatingRow {
  movie_id: string;
  rating: number;
  created_at: string;
  updated_at: string | null;
}

function collect<Row, Entry>(
  rows: Row[] | null | undefined,
  toEntry: (row: Row) => [unknown, Entry | null],
): Record<MovieId, Entry> {
  const record: Record<MovieId, Entry> = {};

  for (const row of rows ?? []) {
    const [movieId, entry] = toEntry(row);

    // Rows that cannot be shown (malformed legacy data) are skipped.
    if (isMovieId(movieId) && entry) record[movieId] = entry;
  }

  return record;
}

/** Map database rows to a snapshot. */
export function rowsToLibrary(
  watchlist: WatchlistRow[] | null | undefined,
  watched: WatchedRow[] | null | undefined,
  ratings: RatingRow[] | null | undefined,
): LibrarySnapshot {
  return {
    watchlist: collect<WatchlistRow, WatchlistEntry>(watchlist, (row) => {
      const addedAt = toIsoTimestamp(row.added_at);

      return [row.movie_id, addedAt ? { addedAt } : null];
    }),
    watched: collect<WatchedRow, WatchedEntry>(watched, (row) => {
      const watchedAt = toIsoTimestamp(row.watched_at);

      return [row.movie_id, watchedAt ? { watchedAt } : null];
    }),
    ratings: collect<RatingRow, RatingEntry>(ratings, (row) => {
      const ratedAt =
        toIsoTimestamp(row.updated_at) ?? toIsoTimestamp(row.created_at);

      return [
        row.movie_id,
        ratedAt && isRatingValue(row.rating)
          ? { rating: row.rating, ratedAt }
          : null,
      ];
    }),
  };
}

function parseRecord<Entry>(
  value: unknown,
  parseEntry: (raw: Record<string, unknown>) => Entry | null,
): Record<MovieId, Entry> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record: Record<MovieId, Entry> = {};

  for (const [movieId, raw] of Object.entries(value)) {
    if (!isMovieId(movieId) || !raw || typeof raw !== "object") continue;
    const entry = parseEntry(raw as Record<string, unknown>);

    if (entry) record[movieId] = entry;
  }

  return record;
}

/**
 * Validate untrusted data (e.g. from localStorage) as a snapshot.
 * Returns null when the shape is wrong; drops individual malformed entries.
 */
export function parseLibrarySnapshot(value: unknown): LibrarySnapshot | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const watchlist = parseRecord<WatchlistEntry>(raw.watchlist, (entry) => {
    const addedAt = toIsoTimestamp(entry.addedAt);

    return addedAt ? { addedAt } : null;
  });
  const watched = parseRecord<WatchedEntry>(raw.watched, (entry) => {
    const watchedAt = toIsoTimestamp(entry.watchedAt);

    return watchedAt ? { watchedAt } : null;
  });
  const ratings = parseRecord<RatingEntry>(raw.ratings, (entry) => {
    const ratedAt = toIsoTimestamp(entry.ratedAt);

    return ratedAt && isRatingValue(entry.rating)
      ? { rating: entry.rating, ratedAt }
      : null;
  });

  if (!watchlist || !watched || !ratings) return null;

  return { watchlist, watched, ratings };
}
